import { createContext, useContext, useSyncExternalStore } from "react";

export interface RecordingScope {
  readonly encounterId?: string | undefined;
  readonly patientId?: string | undefined;
  readonly label?: string | undefined;
  readonly active: boolean;
}

/** Each keep-alive pane supplies its own identity and visibility. */
export const RecordingScopeContext = createContext<RecordingScope>({ active: true });
export const useRecordingScope = () => useContext(RecordingScopeContext);

export interface MicrophoneOwner extends Omit<RecordingScope, "active"> {
  readonly token: symbol;
}
export interface MicrophoneLease {
  readonly ready: Promise<void>;
  release(): void;
}
let owner: MicrophoneOwner | null = null;
const listeners = new Set<() => void>();
const emit = () => {
  for (const listener of listeners) listener();
};
export const microphoneOwner = () => owner;
export function useMicrophoneOwner() {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    microphoneOwner,
    () => null,
  );
}

/**
 * Claim synchronously before asking permission, so two buttons cannot race.
 * Keep the lease through stopping/transcription to avoid switching patients
 * while audio is still draining. Web Locks also excludes other tabs on this
 * origin when the browser supports it; no patient information leaves memory.
 */
export function claimMicrophone(scope: RecordingScope): MicrophoneLease {
  if (!scope.active) throw new Error("Open this encounter before starting its microphone.");
  if (owner) {
    throw new Error(
      "Another microphone session is active. Stop it and wait for processing to finish first.",
    );
  }
  const next = {
    token: Symbol("microphone"),
    encounterId: scope.encounterId,
    patientId: scope.patientId,
    label: scope.label,
  };
  owner = next;
  emit();
  let released = false;
  let releaseBrowserLock: (() => void) | undefined;
  const release = () => {
    released = true;
    releaseBrowserLock?.();
    if (owner === next) {
      owner = null;
      emit();
    }
  };
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  const ready = locks
    ? new Promise<void>((resolve, reject) => {
        void locks
          .request("forma-clinical-microphone", { ifAvailable: true }, async (lock) => {
            if (!lock)
              throw new Error(
                "A microphone session is active in another browser tab. Stop it before recording here.",
              );
            if (released) return resolve();
            const held = new Promise<void>((done) => {
              releaseBrowserLock = done;
            });
            resolve();
            await held;
          })
          .catch((error: unknown) => {
            release();
            reject(error);
          });
      })
    : Promise.resolve();
  return { ready, release };
}
